import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X } from 'lucide-react';
import { CardGridSkeleton } from '../../components/common/Skeleton';
import { PageFadeSection } from '../../components/common/PageFadeSection';
import toast from 'react-hot-toast';
import { UserRole } from '../../constants/userRole';
import {
    assignTask,
    getMyTasks,
    getMyTaskStats,
    updateTaskStatus,
    logTaskTime,
    getAllUsers,
    getTaskById,
    updateTask,
    TaskAssignmentStatus,
    TaskPriority,
    getStatusName,
    getPriorityName,
    getStatusBadgeColor,
    getPriorityBadgeColor
} from '../../api/taskAssignmentApi';
import { parseUTC } from '../../utils/dateUtils';

const MyAssignedTasksPage = () => {
    // State for main tasks page
    const [tasks, setTasks] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedTask, setSelectedTask] = useState(null);
    const [showTaskDetailModal, setShowTaskDetailModal] = useState(false);
    const [showCreateTask, setShowCreateTask] = useState(false);
    const [users, setUsers] = useState([]);
    const [statusFilter, setStatusFilter] = useState(null);
    const [updating, setUpdating] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [timeLogging, setTimeLogging] = useState(false);

    // State for edit modal
    const [editFormData, setEditFormData] = useState({
        id: '',
        productionLotId: '',
        productionLotName: '',
        description: '',
        assignedToUserIds: [],
        priority: TaskPriority.Normal,
        dueDate: '',
        notes: '',
        status: null,
        hoursLogged: 0,
        timeLogNotes: '',
        newHours: '',
        newNotes: ''
    });

    // State for create task
    const [createFormData, setCreateFormData] = useState({
        productionLotId: '',
        productionLotName: '',
        description: '',
        assignedToUserIds: [],
        priority: TaskPriority.Normal,
        dueDate: '',
        notes: ''
    });

    // State for create task modal
    const [userSearchTerm, setUserSearchTerm] = useState('');
    const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
    const [usersLoading, setUsersLoading] = useState(false);

    // Get current user info
    const [currentUserRole, setCurrentUserRole] = useState(0);
    const [currentUserId, setCurrentUserId] = useState('');

    // Refs to prevent duplicate API calls
    const hasLoadedTasksRef = useRef(false);
    const isUsersLoadingRef = useRef(false);

    // Format date for assignees
    const formatAssigneeAssignedDate = (assignedAt) => {
        if (!assignedAt || assignedAt === '0001-01-01T00:00:00' || assignedAt === '0001-01-01T00:00:00.0000000') {
            return 'Not Assigned Yet';
        }
        
        try {
            const date = parseUTC(assignedAt);
            if (isNaN(date.getTime())) return 'Not assigned yet';
            
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            });
        } catch (err) {
            return 'Not assigned yet';
        }
    };

    // Load main data - FIXED to prevent duplicate calls
    useEffect(() => {
        // Only run if we haven't loaded yet or if statusFilter changes
        if (hasLoadedTasksRef.current && statusFilter === null) return;

        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        setCurrentUserRole(userData.role || 0);
        setCurrentUserId(userData.id || '');

        loadTasksData();
        
        // Mark as loaded only on initial load (when statusFilter is null)
        if (statusFilter === null) {
            hasLoadedTasksRef.current = true;
        }

        return () => {
            // Cleanup on unmount
            hasLoadedTasksRef.current = false;
        };
    }, [statusFilter]); // Only reload when statusFilter changes

    // Load users when modals open - FIXED to prevent duplicate calls
    useEffect(() => {
        // Only load users if modal is open AND users array is empty AND not already loading
        if ((showCreateTask || showTaskDetailModal) && 
            users.length === 0 && 
            !isUsersLoadingRef.current) {
            
            const loadUsersData = async () => {
                isUsersLoadingRef.current = true;
                setUsersLoading(true);
                await loadUsers();
                setUsersLoading(false);
                isUsersLoadingRef.current = false;
            };
            
            loadUsersData();
        }
    }, [showCreateTask, showTaskDetailModal]); // Remove users.length from dependency

    const loadTasksData = async () => {
        try {
            setLoading(true);
            console.log('Loading tasks data...');
            
            const [tasksResult, statsResult] = await Promise.all([
                getMyTasks(statusFilter),
                getMyTaskStats()
            ]);

            if (tasksResult.isSuccess) {
                const tasksWithOverdue = (tasksResult.data || []).map(task => ({
                    ...task,
                    isOverdue: task.dueDate &&
                        new Date(task.dueDate) < new Date() &&
                        task.status !== TaskAssignmentStatus.Completed &&
                        task.status !== TaskAssignmentStatus.Cancelled,
                    // Format assignee dates
                    assignees: task.assignees?.map(assignee => ({
                        ...assignee,
                        formattedAssignedAt: formatAssigneeAssignedDate(assignee.assignedAt)
                    })) || []
                }));
                setTasks(tasksWithOverdue);
            } else {
                toast.error(tasksResult.message || 'Failed to load tasks');
            }

            if (statsResult.isSuccess) {
                setStats(statsResult.data);
            } else {
                toast.error(statsResult.message || 'Failed to load stats');
            }

        } catch (err) {
            const errorMessage = err.response?.data?.message ||
                err.response?.data?.errors?.join(', ') ||
                err.message ||
                'Failed to load data';
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const loadUsers = async () => {
        try {
            console.log('Loading users...');
            const result = await getAllUsers();
            if (result.isSuccess && result.data) {
                // Filter active users only
                const activeUsers = result.data.filter(user => user.isActive);
                setUsers(activeUsers);
            } else {
                toast.error(result.message || 'Failed to load users');
            }
        } catch (err) {
            const errorMessage = err.response?.data?.message ||
                err.response?.data?.errors?.join(', ') ||
                err.message ||
                'Failed to load users';
            toast.error(errorMessage);
        }
    };

    // ========== CREATE TASK FUNCTIONALITY ==========

    // Filter users based on search term
    const getFilteredUsers = () => {
        if (!userSearchTerm.trim()) return users;

        const searchTerm = userSearchTerm.toLowerCase();
        return users.filter(user => {
            const fullName = user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim().toLowerCase();
            const email = user.email?.toLowerCase() || '';
            const department = user.department?.toLowerCase() || '';

            return (
                fullName.includes(searchTerm) ||
                email.includes(searchTerm) ||
                department.includes(searchTerm)
            );
        });
    };

    // Handle create task
    const handleCreateTask = async (e) => {
        e.preventDefault();

        if (!createFormData.productionLotId.trim()) {
            toast.error('Production Lot ID is required');
            return;
        }

        if (createFormData.assignedToUserIds.length === 0) {
            toast.error('Please select at least one user');
            return;
        }

        const createPromise = new Promise(async (resolve, reject) => {
            try {
                setSubmitting(true);

                // Format the dueDate properly if it exists
                let formattedDueDate = null;
                if (createFormData.dueDate) {
                    const date = new Date(createFormData.dueDate);
                    formattedDueDate = date.toISOString();
                }

                const taskData = {
                    productionLotId: createFormData.productionLotId.trim(),
                    productionLotName: createFormData.productionLotName?.trim() || '',
                    description: createFormData.description?.trim() || '',
                    assignedToUserIds: createFormData.assignedToUserIds,
                    priority: createFormData.priority,
                    dueDate: formattedDueDate,
                    notes: createFormData.notes?.trim() || ''
                };

                // Remove empty optional fields
                if (!taskData.productionLotName) delete taskData.productionLotName;
                if (!taskData.description) delete taskData.description;
                if (!taskData.dueDate) delete taskData.dueDate;
                if (!taskData.notes) delete taskData.notes;

                console.log('DEBUG - Final payload being sent:', JSON.stringify(taskData, null, 2));

                const result = await assignTask(taskData);

                console.log('DEBUG - API Response:', result);

                if (result.isSuccess) {
                    setCreateFormData({
                        productionLotId: '',
                        productionLotName: '',
                        description: '',
                        assignedToUserIds: [],
                        priority: TaskPriority.Normal,
                        dueDate: '',
                        notes: ''
                    });

                    setTimeout(() => {
                        setShowCreateTask(false);
                        setUserSearchTerm('');
                        setIsUserDropdownOpen(false);
                        loadTasksData();
                    }, 1000);

                    resolve('Task assigned successfully!');
                } else {
                    // Provide more detailed error message
                    let errorMsg = result.message || 'Failed to assign task';

                    if (errorMsg.includes('Assigner not found')) {
                        errorMsg = `Authentication issue: The system cannot find user with ID: ${currentUserId}. \nPossible reasons:\n1. User doesn't exist in task assignment service\n2. User ID format mismatch\n3. User is inactive\n\nPlease contact support.`;
                    }

                    reject(new Error(errorMsg));
                }
            } catch (err) {
                console.error('Full error object:', err);

                let errorMessage = 'Failed to assign task';

                if (err.response?.data) {
                    const errorData = err.response.data;
                    console.error('Error response data:', errorData);

                    if (errorData.message) {
                        errorMessage = errorData.message;
                    }
                } else if (err.message) {
                    errorMessage = err.message;
                }

                console.error('Error assigning task:', errorMessage);
                reject(new Error(errorMessage));
            } finally {
                setSubmitting(false);
            }
        });

        toast.promise(createPromise, {
            loading: 'Assigning task...',
            success: (message) => message,
            error: (err) => {
                console.error('Toast error:', err.message);
                return (
                    <div>
                        {err.message.split('\n').map((line, index) => (
                            <div key={index}>{line}</div>
                        ))}
                    </div>
                );
            },
        });
    };

    // User selection handlers for create modal
    const handleUserSelect = (userId) => {
        const isSelected = createFormData.assignedToUserIds.includes(userId);

        if (isSelected) {
            // Deselect user
            setCreateFormData(prev => ({
                ...prev,
                assignedToUserIds: prev.assignedToUserIds.filter(id => id !== userId)
            }));
        } else {
            // Select user
            setCreateFormData(prev => ({
                ...prev,
                assignedToUserIds: [...prev.assignedToUserIds, userId]
            }));
        }
    };

    const handleSelectAllUsers = () => {
        if (createFormData.assignedToUserIds.length === users.length) {
            // Deselect all
            setCreateFormData(prev => ({
                ...prev,
                assignedToUserIds: []
            }));
        } else {
            // Select all users (or all filtered users if searching)
            const usersToSelect = getFilteredUsers();
            const userIds = usersToSelect.map(user => user.id);
            setCreateFormData(prev => ({
                ...prev,
                assignedToUserIds: [...new Set([...prev.assignedToUserIds, ...userIds])]
            }));
        }
    };

    // Remove selected user from list
    const removeSelectedUser = (userId) => {
        setCreateFormData(prev => ({
            ...prev,
            assignedToUserIds: prev.assignedToUserIds.filter(id => id !== userId)
        }));
    };

    // Clear all selected users
    const clearAllSelected = () => {
        setCreateFormData(prev => ({
            ...prev,
            assignedToUserIds: []
        }));
    };

    // Helper functions needed for the user selection
    const getSelectedUsersInfo = () => {
        return users
            .filter(user => createFormData.assignedToUserIds.includes(user.id))
            .map(user => {
                const name = user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim();
                const initials = name
                    .split(' ')
                    .map(n => n[0])
                    .join('')
                    .slice(0, 2)
                    .toUpperCase();
                return {
                    id: user.id,
                    name,
                    email: user.email || '',
                    initials
                };
            });
    };

    // ========== EDIT TASK FUNCTIONALITY ==========

    // Open Task Detail Modal
    const openTaskDetailModal = async (task) => {
        try {
            console.log('DEBUG: Opening modal for task:', {
                id: task.id,
                name: task.productionLotName || task.productionLotId,
                cardStatus: task.status,
                cardStatusName: getStatusName(task.status),
                assignees: task.assignees?.length || 0
            });

            // Format date for input field
            let formattedDueDate = '';
            if (task.dueDate) {
                const date = new Date(task.dueDate);
                formattedDueDate = date.toISOString().split('T')[0];
            }

            // Get assignee IDs from task data (including formatted dates)
            const assigneeIds = task.assignees?.map(a => a.userId) || [];

            // Set the form data with the card's status
            const formData = {
                id: task.id,
                productionLotId: task.productionLotId || '',
                productionLotName: task.productionLotName || '',
                description: task.description || '',
                assignedToUserIds: assigneeIds,
                priority: task.priority || TaskPriority.Normal,
                status: task.status, // Use the card's status
                dueDate: formattedDueDate,
                notes: task.notes || '',
                hoursLogged: task.actualHours || 0,
                timeLogNotes: '',
                newHours: '',
                newNotes: ''
            };

            console.log('DEBUG: Setting editFormData with:', {
                status: formData.status,
                statusName: getStatusName(formData.status),
                assigneeCount: formData.assignedToUserIds.length
            });

            setSelectedTask(task);
            setEditFormData(formData);
            setShowTaskDetailModal(true);

            // Load fresh data in background
            loadFreshTaskData(task.id);
            
        } catch (err) {
            toast.error('Failed to open task details');
            console.error(err);
        }
    };

    // Load fresh task data in background
    const loadFreshTaskData = async (taskId) => {
        try {
            const result = await getTaskById(taskId);
            if (result.isSuccess) {
                const taskDetails = result.data;
                
                console.log('DEBUG: API returned fresh data:', {
                    apiStatus: taskDetails.status,
                    apiStatusName: getStatusName(taskDetails.status),
                    assignees: taskDetails.assignees,
                    assigneeCount: taskDetails.assignees?.length || 0
                });

                // Format assignee dates
                const taskDetailsWithFormattedDates = {
                    ...taskDetails,
                    assignees: taskDetails.assignees?.map(assignee => ({
                        ...assignee,
                        formattedAssignedAt: formatAssigneeAssignedDate(assignee.assignedAt)
                    })) || []
                };

                // Update selected task
                setSelectedTask(taskDetailsWithFormattedDates);

                // Get fresh assignee IDs
                const freshAssigneeIds = taskDetails.assignees?.map(a => a.userId) || [];

                // Update form data but preserve the existing status
                setEditFormData(prev => ({
                    ...prev,
                    productionLotId: taskDetails.productionLotId || prev.productionLotId,
                    productionLotName: taskDetails.productionLotName || prev.productionLotName,
                    description: taskDetails.description || prev.description,
                    assignedToUserIds: freshAssigneeIds,
                    priority: taskDetails.priority || prev.priority,
                    notes: taskDetails.notes || prev.notes,
                    hoursLogged: taskDetails.actualHours || prev.hoursLogged
                    // Do NOT update status - keep what was set from the card
                }));
            }
        } catch (err) {
            console.log('Background data refresh failed:', err);
        }
    };

    // Handle Task Update (Status, Time, Notes, Assignees)
    const handleTaskUpdate = async (e) => {
        e.preventDefault();

        // Validate status is set
        if (editFormData.status === null || editFormData.status === undefined) {
            toast.error('Task status is required');
            return;
        }

        const updatePromise = new Promise(async (resolve, reject) => {
            try {
                setUpdating(true);

                // Format the dueDate properly if it exists
                let formattedDueDate = null;
                if (editFormData.dueDate) {
                    const date = new Date(editFormData.dueDate);
                    formattedDueDate = date.toISOString();
                }

                const taskData = {
                    id: editFormData.id,
                    productionLotId: editFormData.productionLotId.trim(),
                    productionLotName: editFormData.productionLotName?.trim() || '',
                    description: editFormData.description?.trim() || '',
                    assignedToUserIds: editFormData.assignedToUserIds,
                    priority: editFormData.priority,
                    status: editFormData.status, // Use the current status
                    dueDate: formattedDueDate,
                    notes: editFormData.notes?.trim() || ''
                };

                // Debug logging
                console.log('DEBUG: Sending task update:', {
                    ...taskData,
                    assigneeCount: taskData.assignedToUserIds?.length || 0
                });

                // Remove empty optional fields
                if (!taskData.productionLotName) delete taskData.productionLotName;
                if (!taskData.description) delete taskData.description;
                if (!taskData.dueDate) delete taskData.dueDate;
                if (!taskData.notes) delete taskData.notes;

                // Log time if new hours entered
                if (editFormData.newHours && parseFloat(editFormData.newHours) > 0) {
                    console.log('DEBUG: Logging time:', {
                        hours: editFormData.newHours,
                        notes: editFormData.newNotes
                    });
                    await logTaskTime(editFormData.id, parseFloat(editFormData.newHours), editFormData.newNotes || '');
                }

                // Update task
                const result = await updateTask(editFormData.id, taskData);

                if (result.isSuccess) {
                    // Reset form
                    setEditFormData({
                        id: '',
                        productionLotId: '',
                        productionLotName: '',
                        description: '',
                        assignedToUserIds: [],
                        priority: TaskPriority.Normal,
                        dueDate: '',
                        notes: '',
                        status: null,
                        hoursLogged: 0,
                        timeLogNotes: '',
                        newHours: '',
                        newNotes: ''
                    });

                    setShowTaskDetailModal(false);
                    setSelectedTask(null);

                    await loadTasksData();
                    resolve('Task updated successfully!');
                } else {
                    reject(new Error(result.message || 'Failed to update task'));
                }
            } catch (err) {
                const errorMessage = err.response?.data?.message ||
                    err.response?.data?.errors?.join(', ') ||
                    err.message ||
                    'Failed to update task';
                reject(new Error(errorMessage));
            } finally {
                setUpdating(false);
            }
        });

        toast.promise(updatePromise, {
            loading: 'Updating task...',
            success: (message) => message,
            error: (err) => err.message || 'Failed to update task',
        });
    };

    // Handle user selection in edit modal
    const handleEditUserSelect = (userId) => {
        const isSelected = editFormData.assignedToUserIds.includes(userId);

        if (isSelected) {
            // Deselect user
            setEditFormData(prev => ({
                ...prev,
                assignedToUserIds: prev.assignedToUserIds.filter(id => id !== userId)
            }));
        } else {
            // Select user
            setEditFormData(prev => ({
                ...prev,
                assignedToUserIds: [...prev.assignedToUserIds, userId]
            }));
        }
    };

    // Get edit selected users info
    const getEditSelectedUsersInfo = () => {
        return users
            .filter(user => editFormData.assignedToUserIds.includes(user.id))
            .map(user => {
                const name = user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim();
                const initials = name
                    .split(' ')
                    .map(n => n[0])
                    .join('')
                    .slice(0, 2)
                    .toUpperCase();
                return {
                    id: user.id,
                    name,
                    email: user.email || '',
                    initials
                };
            });
    };

    // ========== QUICK ACTIONS ==========

    // Handle Quick Status Change
    const handleQuickStatusChange = async (task, newStatus) => {
        const statusPromise = new Promise(async (resolve, reject) => {
            try {
                const result = await updateTaskStatus(task.id, newStatus);
                if (result.isSuccess) {
                    await loadTasksData();
                    resolve(`Task status updated to ${getStatusName(newStatus)}`);
                } else {
                    reject(new Error(result.message || 'Failed to update status'));
                }
            } catch (err) {
                const errorMessage = err.response?.data?.message ||
                    err.response?.data?.errors?.join(', ') ||
                    err.message ||
                    'Failed to update status';
                reject(new Error(errorMessage));
            }
        });

        toast.promise(statusPromise, {
            loading: 'Updating status...',
            success: (message) => message,
            error: (err) => err.message || 'Failed to update status',
        });
    };

    // ========== HELPER FUNCTIONS ==========

    // Format date helper
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = parseUTC(dateString);
            if (isNaN(date.getTime())) return '-';
            
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (err) {
            return '-';
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString || dateString === '0001-01-01T00:00:00') return '-';
        try {
            const date = parseUTC(dateString);
            if (isNaN(date.getTime())) return '-';
            
            return date.toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            });
        } catch (err) {
            return '-';
        }
    };

    // Get filtered status values (only numbers)
    const getStatusValues = () => {
        return [0, 1, 2, 3, 4]; // Directly use the enum values
    };

    // Get user role name and badge
    const getUserRoleName = (roleValue) => {
        // Create a reverse mapping from value to name
        const roleMap = {};
        
        for (const [name, value] of Object.entries(UserRole)) {
            roleMap[value] = name;
        }
        
        return roleMap[roleValue] || 'User';
    };

    const getUserRoleBadge = (role) => {
        const badges = {
            0: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-600',
            1: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 border border-blue-200 dark:border-blue-800',
            2: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-800',
            3: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-400 border border-purple-200 dark:border-purple-800',
            4: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400 border border-red-200 dark:border-red-800'
        };
        return badges[role] || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
    };

    // ========== RENDER FUNCTIONS ==========

    // Render Task Cards
    const renderTaskCards = () => {
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {tasks.map((task) => (
                    <div
                        key={task.id}
                        onClick={() => openTaskDetailModal(task)}
                        className={`bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer border-l-4 
                            ${task.isOverdue ? 'border-red-500' :
                                task.status === TaskAssignmentStatus.Completed ? 'border-green-500' :
                                    task.status === TaskAssignmentStatus.InProgress ? 'border-blue-500' :
                                        'border-gray-300 dark:border-gray-600'}`}
                    >
                        <div className="p-6">
                            {/* Header with Status Badge */}
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h3 className="font-semibold text-gray-800 dark:text-gray-200 text-lg mb-2">
                                        {task.productionLotName || task.productionLotId}
                                    </h3>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Status:</span>
                                        <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusBadgeColor(task.status)}`}>
                                            {getStatusName(task.status)}
                                        </span>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="flex items-center justify-end gap-2 mb-2">
                                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Priority:</span>
                                        <span className={`px-3 py-1 text-xs font-medium rounded-full ${getPriorityBadgeColor(task.priority)}`}>
                                            {getPriorityName(task.priority)}
                                        </span>
                                    </div>
                                    {task.isOverdue && (
                                        <div className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400">
                                            <i className="fas fa-exclamation-circle text-xs"></i>
                                            Overdue
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Quick Info */}
                            <div className="space-y-2 mb-4">
                                <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                                    <i className="fas fa-user text-gray-400 dark:text-gray-500 mr-2 w-4"></i>
                                    <span className="truncate">{task.assignedByName || 'System'}</span>
                                </div>
                                <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                                    <i className="fas fa-calendar text-gray-400 dark:text-gray-500 mr-2 w-4"></i>
                                    <span>Due: {task.dueDate ? formatDate(task.dueDate) : 'No due date'}</span>
                                </div>
                                <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                                    <i className="fas fa-hourglass text-gray-400 dark:text-gray-500 mr-2 w-4"></i>
                                    <span>Hours: {task.actualHours?.toFixed(1) || 0}</span>
                                </div>
                                <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                                    <i className="fas fa-users text-gray-400 dark:text-gray-500 mr-2 w-4"></i>
                                    <span>Assignees: {task.assignees?.length || 0}</span>
                                </div>
                            </div>

                            {/* Quick Actions based on status */}
                            <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
                                {task.status === TaskAssignmentStatus.Assigned && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleQuickStatusChange(task, TaskAssignmentStatus.InProgress);
                                        }}
                                        className="w-full px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-sm font-medium rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200"
                                    >
                                        <i className="fas fa-play mr-2"></i>
                                        Start Working
                                    </button>
                                )}
                                {task.status === TaskAssignmentStatus.InProgress && (
                                    <div className="flex space-x-2">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleQuickStatusChange(task, TaskAssignmentStatus.Completed);
                                            }}
                                            className="flex-1 px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white text-sm font-medium rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200"
                                        >
                                            <i className="fas fa-check mr-2"></i>
                                            Complete
                                        </button>
                                    </div>
                                )}
                                {task.status === TaskAssignmentStatus.Completed && (
                                    <div className="text-center text-sm text-gray-500 dark:text-gray-400">
                                        <i className="fas fa-check-circle text-green-500 mr-2"></i>
                                        Completed
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    // Render Create Task Modal
    const renderCreateTaskModal = () => {
        if (!showCreateTask) return null;

        return (
            <div className="fixed inset-0 z-50 overflow-y-auto">
                {/* Backdrop */}
                <div
                    className="fixed inset-0 bg-black/30 backdrop-blur-sm transition-opacity"
                    onClick={() => {
                        setShowCreateTask(false);
                        setCreateFormData({
                            productionLotId: '',
                            productionLotName: '',
                            description: '',
                            assignedToUserIds: [],
                            priority: TaskPriority.Normal,
                            dueDate: '',
                            notes: ''
                        });
                        setUserSearchTerm('');
                        setIsUserDropdownOpen(false);
                    }}
                />

                {/* Modal Container */}
                <div className="flex min-h-full items-center justify-center p-4">
                    <div className="relative w-full max-w-4xl bg-white dark:bg-gray-800 rounded-xl shadow-2xl overflow-hidden">
                        {/* Modal Header */}
                        <div className="relative bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-5">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h2 className="text-2xl font-bold text-white">Assign New Task</h2>
                                    <p className="text-blue-100 text-base">Create and assign tasks to team members</p>
                                </div>
                                <button
                                    onClick={() => {
                                        setShowCreateTask(false);
                                        setCreateFormData({
                                            productionLotId: '',
                                            productionLotName: '',
                                            description: '',
                                            assignedToUserIds: [],
                                            priority: TaskPriority.Normal,
                                            dueDate: '',
                                            notes: ''
                                        });
                                        setUserSearchTerm('');
                                        setIsUserDropdownOpen(false);
                                    }}
                                    className="text-white/90 hover:text-white p-2 hover:bg-white/20 rounded-lg transition-all duration-200"
                                >
                                    <i className="fas fa-times text-xl"></i>
                                </button>
                            </div>
                        </div>

                        <div className="p-6 overflow-y-auto max-h-[70vh]">
                            {usersLoading ? (
                                <div className="flex justify-center items-center h-48">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                </div>
                            ) : (
                                <form onSubmit={handleCreateTask}>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Production Lot ID *
                                            </label>
                                            <input
                                                type="text"
                                                value={createFormData.productionLotId}
                                                onChange={(e) => setCreateFormData({ ...createFormData, productionLotId: e.target.value })}
                                                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                                                placeholder="Enter lot ID"
                                                required
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Production Lot Name
                                            </label>
                                            <input
                                                type="text"
                                                value={createFormData.productionLotName}
                                                onChange={(e) => setCreateFormData({ ...createFormData, productionLotName: e.target.value })}
                                                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                                                placeholder="Enter lot name"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Priority
                                            </label>
                                            <select
                                                value={createFormData.priority}
                                                onChange={(e) => setCreateFormData({ ...createFormData, priority: parseInt(e.target.value) })}
                                                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                                            >
                                                <option value={TaskPriority.Low}>Low Priority</option>
                                                <option value={TaskPriority.Normal}>Normal Priority</option>
                                                <option value={TaskPriority.High}>High Priority</option>
                                                <option value={TaskPriority.Urgent}>Urgent Priority</option>
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Due Date
                                            </label>
                                            <input
                                                type="date"
                                                value={createFormData.dueDate}
                                                onChange={(e) => setCreateFormData({ ...createFormData, dueDate: e.target.value })}
                                                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                                                min={new Date().toISOString().split('T')[0]}
                                            />
                                        </div>
                                    </div>

                                    <div className="mb-6">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Description
                                        </label>
                                        <textarea
                                            value={createFormData.description}
                                            onChange={(e) => setCreateFormData({ ...createFormData, description: e.target.value })}
                                            rows={3}
                                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                                            placeholder="Enter task description"
                                        />
                                    </div>

                                    {/* Assign To Users Section with Search */}
                                    <div className="mb-6">
                                        <div className="flex justify-between items-center mb-2">
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                Assign To Users *
                                            </label>
                                            <button
                                                type="button"
                                                onClick={handleSelectAllUsers}
                                                className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                                            >
                                                {createFormData.assignedToUserIds.length === users.length ? 'Deselect All' : 'Select All'}
                                            </button>
                                        </div>

                                        {/* Search Input */}
                                        <div className="mb-3">
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    value={userSearchTerm}
                                                    onChange={(e) => {
                                                        setUserSearchTerm(e.target.value);
                                                        setIsUserDropdownOpen(true);
                                                    }}
                                                    onFocus={() => setIsUserDropdownOpen(true)}
                                                    className="w-full px-4 py-3 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                                                    placeholder="Search users by name, email, or department..."
                                                />
                                                <i className="fas fa-search absolute left-3 top-3.5 text-gray-400"></i>
                                                {userSearchTerm && (
                                                    <button
                                                        onClick={() => setUserSearchTerm('')}
                                                        className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                                        type="button"
                                                    >
                                                        <i className="fas fa-times"></i>
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        {/* Selected Users Preview */}
                                        {createFormData.assignedToUserIds.length > 0 && (
                                            <div className="mb-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="text-sm font-medium text-blue-800 dark:text-blue-400">
                                                        Selected ({createFormData.assignedToUserIds.length})
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={clearAllSelected}
                                                        className="text-sm text-red-600 hover:text-red-800"
                                                    >
                                                        Clear All
                                                    </button>
                                                </div>
                                                <div className="flex flex-wrap gap-2">
                                                    {getSelectedUsersInfo().map(user => (
                                                        <div
                                                            key={user.id}
                                                            className="flex items-center gap-2 bg-white dark:bg-gray-700 px-3 py-2 rounded-lg border border-blue-300 dark:border-blue-700"
                                                        >
                                                            <div className="h-6 w-6 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                                                                <span className="text-xs font-medium text-blue-800 dark:text-blue-400">{user.initials}</span>
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-sm text-gray-800 dark:text-gray-200 truncate max-w-[120px]">{user.name}</p>
                                                                <p className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[120px]">{user.email}</p>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() => removeSelectedUser(user.id)}
                                                                className="ml-2 text-gray-400 hover:text-red-600"
                                                            >
                                                                <i className="fas fa-times text-sm"></i>
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Users List */}
                                        {users.length === 0 ? (
                                            <p className="text-gray-500 dark:text-gray-400 text-sm">No users found</p>
                                        ) : (
                                            <div className="relative">
                                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                                                    {getFilteredUsers().map(user => {
                                                        const fullName = user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim();
                                                        const isSelected = createFormData.assignedToUserIds.includes(user.id);
                                                        return (
                                                            <div
                                                                key={user.id}
                                                                className={`flex items-center p-3 rounded-lg cursor-pointer transition-all duration-200 ${isSelected
                                                                        ? 'bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-500'
                                                                        : 'hover:bg-gray-50 dark:hover:bg-gray-700 border-2 border-transparent'
                                                                    }`}
                                                                onClick={() => handleUserSelect(user.id)}
                                                            >
                                                                <div className="flex items-center mr-3">
                                                                    <div className={`h-5 w-5 rounded border flex items-center justify-center mr-3 ${isSelected
                                                                            ? 'bg-blue-500 border-blue-500'
                                                                            : 'bg-white dark:bg-gray-600 border-gray-300 dark:border-gray-500'
                                                                        }`}>
                                                                        {isSelected && (
                                                                            <i className="fas fa-check text-white text-xs"></i>
                                                                        )}
                                                                    </div>
                                                                    <div className="h-8 w-8 bg-gray-100 dark:bg-gray-600 rounded-full flex items-center justify-center">
                                                                        <span className="text-gray-600 dark:text-gray-300 font-semibold text-xs">
                                                                            {fullName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="flex items-center gap-1 mb-1">
                                                                        <div className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                                                                            {fullName}
                                                                        </div>
                                                                        {/* Show role badge for ALL users */}
                                                                        <span className={`px-1.5 py-0.5 text-xs font-medium rounded-full ${getUserRoleBadge(user.role)}`}>
                                                                            {getUserRoleName(user.role)}
                                                                        </span>
                                                                    </div>
                                                                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                                                        {user.email} • {user.department || 'No Department'}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}

                                                    {/* No Results Message */}
                                                    {userSearchTerm && getFilteredUsers().length === 0 && (
                                                        <div className="col-span-full text-center py-4">
                                                            <i className="fas fa-search text-gray-300 dark:text-gray-600 text-2xl mb-2"></i>
                                                            <p className="text-sm text-gray-600 dark:text-gray-400">No users found matching "{userSearchTerm}"</p>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* User Count */}
                                                <div className="mt-3 flex items-center justify-between">
                                                    <div className="text-sm text-gray-600 dark:text-gray-400">
                                                        {getFilteredUsers().length === users.length ? (
                                                            <span>Showing all {users.length} users</span>
                                                        ) : (
                                                            <span>Showing {getFilteredUsers().length} of {users.length} users</span>
                                                        )}
                                                    </div>
                                                    {createFormData.assignedToUserIds.length > 0 && (
                                                        <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                                            {createFormData.assignedToUserIds.length} selected
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="mb-8">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Additional Notes
                                        </label>
                                        <textarea
                                            value={createFormData.notes}
                                            onChange={(e) => setCreateFormData({ ...createFormData, notes: e.target.value })}
                                            rows={2}
                                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                                            placeholder="Any additional instructions..."
                                        />
                                    </div>

                                    <div className="flex justify-end space-x-4">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setShowCreateTask(false);
                                                setCreateFormData({
                                                    productionLotId: '',
                                                    productionLotName: '',
                                                    description: '',
                                                    assignedToUserIds: [],
                                                    priority: TaskPriority.Normal,
                                                    dueDate: '',
                                                    notes: ''
                                                });
                                                setUserSearchTerm('');
                                                setIsUserDropdownOpen(false);
                                            }}
                                            className="px-6 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 hover:border-gray-400 transition-all duration-200"
                                            disabled={submitting}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={submitting || createFormData.assignedToUserIds.length === 0}
                                            className="px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg hover:from-blue-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {submitting ? (
                                                <span className="flex items-center">
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                                    Assigning...
                                                </span>
                                            ) : (
                                                `Assign to ${createFormData.assignedToUserIds.length} User${createFormData.assignedToUserIds.length !== 1 ? 's' : ''}`
                                            )}
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Render Task Detail Modal (with edit functionality)
    const renderTaskDetailModal = () => {
        if (!showTaskDetailModal || !selectedTask) return null;

        // Check if status is loaded
        const currentStatus = editFormData.status !== null ? editFormData.status : selectedTask.status;

        return (
            <div className="fixed inset-0 z-50 overflow-y-auto">
                {/* Backdrop */}
                <div
                    className="fixed inset-0 bg-black/30 backdrop-blur-sm transition-opacity"
                    onClick={() => {
                        setShowTaskDetailModal(false);
                        setSelectedTask(null);
                    }}
                />

                {/* Modal Container */}
                <div className="flex min-h-full items-center justify-center p-4">
                    <div className="relative w-full max-w-4xl bg-white dark:bg-gray-800 rounded-xl shadow-2xl overflow-hidden">
                        {/* Modal Header */}
                        <div className="relative bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-5">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h2 className="text-2xl font-bold text-white">Task Details</h2>
                                    <p className="text-blue-100 text-base">View and manage task details</p>
                                </div>
                                <button
                                    onClick={() => {
                                        setShowTaskDetailModal(false);
                                        setSelectedTask(null);
                                    }}
                                    className="text-white/90 hover:text-white p-2 hover:bg-white/20 rounded-lg transition-all duration-200"
                                >
                                    <i className="fas fa-times text-xl"></i>
                                </button>
                            </div>
                        </div>

                        <div className="p-6 overflow-y-auto max-h-[70vh]">
                            {/* Task Information */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                                    <h3 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Task Information</h3>
                                    <div className="space-y-3">
                                        <div>
                                            <label className="text-sm text-gray-500 dark:text-gray-400">Production Lot ID</label>
                                            <p className="font-medium dark:text-white">{selectedTask.productionLotId}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm text-gray-500 dark:text-gray-400">Lot Name</label>
                                            <p className="font-medium dark:text-white">{selectedTask.productionLotName || 'Not specified'}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm text-gray-500 dark:text-gray-400">Description</label>
                                            <p className="font-medium dark:text-white">{selectedTask.description || 'No description'}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                                    <h3 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Status & Timing</h3>
                                    <div className="space-y-3">
                                        <div className="flex justify-between">
                                            <span className="text-sm text-gray-500 dark:text-gray-400">Current Status</span>
                                            <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusBadgeColor(currentStatus)}`}>
                                                {getStatusName(currentStatus)}
                                            </span>
                                        </div>
                                        <div>
                                            <label className="text-sm text-gray-500 dark:text-gray-400">Priority</label>
                                            <p className="font-medium dark:text-white">{getPriorityName(selectedTask.priority)}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm text-gray-500 dark:text-gray-400">Assigned By</label>
                                            <p className="font-medium dark:text-white">{selectedTask.assignedByName || 'System'}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm text-gray-500 dark:text-gray-400">Created Date</label>
                                            <p className="font-medium dark:text-white">{formatDateTime(selectedTask.assignedAt)}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm text-gray-500 dark:text-gray-400">Due Date</label>
                                            <p className={`font-medium ${selectedTask.isOverdue ? 'text-red-600 dark:text-red-400' : 'dark:text-white'}`}>
                                                {selectedTask.dueDate ? formatDate(selectedTask.dueDate) : 'No due date'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Current Assignees Section */}
                            {selectedTask.assignees && selectedTask.assignees.length > 0 && (
                                <div className="mb-6">
                                    <h3 className="font-semibold text-gray-700 dark:text-gray-300 mb-3">Current Assignees</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {selectedTask.assignees.map((assignee, index) => (
                                            <div key={index} className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg border border-gray-200 dark:border-gray-600">
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <div className="font-medium text-gray-800 dark:text-gray-200">
                                                            {assignee.userName || 'Unknown User'}
                                                        </div>
                                                        <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                                            Status: <span className={`font-medium ${getStatusBadgeColor(assignee.status)} px-2 py-0.5 rounded-full text-xs`}>
                                                                {assignee.statusName || getStatusName(assignee.status)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="text-sm text-gray-500 dark:text-gray-400">
                                                            Assigned: {assignee.formattedAssignedAt || formatAssigneeAssignedDate(assignee.assignedAt)}
                                                        </div>
                                                        <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                                            Hours: {assignee.actualHours?.toFixed(1) || 0}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Hours & Notes Section */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                                    <h3 className="font-semibold text-blue-700 dark:text-blue-400 mb-4">Time Tracking</h3>
                                    <div className="mb-4">
                                        <div className="flex justify-between mb-2">
                                            <span className="text-sm text-blue-600 dark:text-blue-400">Total Hours Logged</span>
                                            <span className="font-bold text-blue-800 dark:text-blue-300">{selectedTask.actualHours?.toFixed(1) || 0} hours</span>
                                        </div>
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                    Add Hours Worked
                                                </label>
                                                <input
                                                    type="number"
                                                    step="0.25"
                                                    min="0"
                                                    value={editFormData.newHours}
                                                    onChange={(e) => setEditFormData({ ...editFormData, newHours: e.target.value })}
                                                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                                    placeholder="E.g., 2.5"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                    Work Notes
                                                </label>
                                                <textarea
                                                    value={editFormData.newNotes}
                                                    onChange={(e) => setEditFormData({ ...editFormData, newNotes: e.target.value })}
                                                    rows={3}
                                                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                                    placeholder="What did you work on?"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border border-yellow-200 dark:border-yellow-800">
                                    <h3 className="font-semibold text-yellow-700 dark:text-yellow-400 mb-4">Task Notes</h3>
                                    <textarea
                                        value={editFormData.notes}
                                        onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                                        rows={8}
                                        className="w-full px-4 py-3 border border-yellow-300 dark:border-yellow-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 bg-white dark:bg-gray-700 dark:text-white"
                                        placeholder="Add or update task notes..."
                                    />
                                    <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-2">
                                        These notes are visible to all assigned users
                                    </p>
                                </div>
                            </div>

                            {/* Update Assignees Section */}
                            <div className="mb-8">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="font-semibold text-gray-700 dark:text-gray-300">Update Assignees</h3>
                                    <span className="text-sm text-gray-500 dark:text-gray-400">
                                        {editFormData.assignedToUserIds.length} user{editFormData.assignedToUserIds.length !== 1 ? 's' : ''} selected
                                    </span>
                                </div>

                                {users.length > 0 ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                                        {users.map(user => {
                                            const fullName = user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim();
                                            const isSelected = editFormData.assignedToUserIds.includes(user.id);
                                            const isCurrentUser = user.id === currentUserId;
                                            
                                            return (
                                                <div
                                                    key={user.id}
                                                    className={`flex items-center p-3 rounded-lg cursor-pointer transition-all duration-200 ${isSelected
                                                            ? 'bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-500'
                                                            : 'hover:bg-gray-50 dark:hover:bg-gray-700 border-2 border-transparent'
                                                        }`}
                                                    onClick={() => handleEditUserSelect(user.id)}
                                                >
                                                    <div className="flex items-center mr-3">
                                                        <div className={`h-5 w-5 rounded border flex items-center justify-center mr-3 ${isSelected
                                                                ? 'bg-blue-500 border-blue-500'
                                                                : 'bg-white dark:bg-gray-600 border-gray-300 dark:border-gray-500'
                                                            }`}>
                                                            {isSelected && (
                                                                <i className="fas fa-check text-white text-xs"></i>
                                                            )}
                                                        </div>
                                                        <div className="h-8 w-8 bg-gray-100 dark:bg-gray-600 rounded-full flex items-center justify-center">
                                                            <span className="text-gray-600 dark:text-gray-300 font-semibold text-xs">
                                                                {fullName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-1 mb-0.5">
                                                            <div className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                                                                {fullName}
                                                            </div>
                                                            {isCurrentUser && (
                                                                <span className="ml-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 px-1.5 py-0.5 rounded">
                                                                    You
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                                                {user.email}
                                                            </div>
                                                            {/* Show role badge for ALL users in edit modal too */}
                                                            <span className={`px-1 py-0.5 text-xs font-medium rounded-full ${getUserRoleBadge(user.role)}`}>
                                                                {getUserRoleName(user.role)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <p className="text-gray-500 dark:text-gray-400 text-center py-4">Loading users...</p>
                                )}
                            </div>

                            {/* Status Update Section */}
                            <div className="mb-8">
                                <h3 className="font-semibold text-gray-700 dark:text-gray-300 mb-4">
                                    Update Status
                                    <span className="text-sm text-gray-500 dark:text-gray-400 font-normal ml-2">
                                        (Current: {getStatusName(currentStatus)})
                                    </span>
                                </h3>
                                <div className="flex flex-wrap gap-3">
                                    {getStatusValues().map(status => (
                                        <button
                                            key={status}
                                            type="button"
                                            onClick={() => setEditFormData({ ...editFormData, status })}
                                            className={`px-4 py-2 rounded-lg transition-all duration-200 ${currentStatus === status
                                                    ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white'
                                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                                }`}
                                        >
                                            {getStatusName(status)}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-gray-600">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowTaskDetailModal(false);
                                        setSelectedTask(null);
                                    }}
                                    className="px-6 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-all duration-200"
                                    disabled={updating}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleTaskUpdate}
                                    disabled={updating || editFormData.status === null}
                                    className="px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {updating ? (
                                        <span className="flex items-center">
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                            Saving...
                                        </span>
                                    ) : (
                                        'Save All Changes'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Main render function
    return (
        <div className="container mx-auto px-4 py-6">
            <PageFadeSection>
            {/* Header */}
            <div className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                    <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                        <i className="fas fa-tasks text-white text-xl"></i>
                    </div>
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">My Tasks</h1>
                        <p className="text-gray-600 dark:text-gray-400">Click on any task to view details and update progress</p>
                        <div className="flex items-center gap-2 mt-1">
                            <span className={`px-1.5 py-0.5 text-xs font-medium rounded-full ${getUserRoleBadge(currentUserRole)}`}>
                                {getUserRoleName(currentUserRole)}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                {currentUserRole === 4 || currentUserRole === 3 ? 'Full Access' :
                                    currentUserRole === 2 ? 'Team Lead Access' : 'View & Update Only'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Stats Cards */}
                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
                        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border dark:border-gray-700">
                            <div className="text-sm text-gray-500 dark:text-gray-400">Total Tasks</div>
                            <div className="text-2xl font-bold dark:text-white">{stats.totalAssigned || 0}</div>
                        </div>
                        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg shadow border border-blue-200 dark:border-blue-800">
                            <div className="text-sm text-blue-600 dark:text-blue-400">In Progress</div>
                            <div className="text-2xl font-bold text-blue-700 dark:text-blue-400">{stats.inProgress || 0}</div>
                        </div>
                        <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg shadow border border-green-200 dark:border-green-800">
                            <div className="text-sm text-green-600 dark:text-green-400">Completed</div>
                            <div className="text-2xl font-bold text-green-700 dark:text-green-400">{stats.completed || 0}</div>
                        </div>
                        <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg shadow border border-red-200 dark:border-red-800">
                            <div className="text-sm text-red-600 dark:text-red-400">Overdue</div>
                            <div className="text-2xl font-bold text-red-700 dark:text-red-400">{stats.overdue || 0}</div>
                        </div>
                        <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg shadow border border-yellow-200 dark:border-yellow-800">
                            <div className="text-sm text-yellow-600 dark:text-yellow-400">Pending</div>
                            <div className="text-2xl font-bold text-yellow-700 dark:text-yellow-400">{stats.pending || 0}</div>
                        </div>
                        <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg shadow border border-purple-200 dark:border-purple-800">
                            <div className="text-sm text-purple-600 dark:text-purple-400">Hours Logged</div>
                            <div className="text-2xl font-bold text-purple-700 dark:text-purple-400">{stats.totalHoursLogged?.toFixed(1) || 0}</div>
                        </div>
                        <div className="bg-indigo-50 p-4 rounded-lg shadow border border-indigo-200">
                            <div className="text-sm text-indigo-600">Avg Hours/Task</div>
                            <div className="text-2xl font-bold text-indigo-700">{stats.averageHoursPerTask?.toFixed(1) || 0}</div>
                        </div>
                        <div className="bg-teal-50 p-4 rounded-lg shadow border border-teal-200">
                            <div className="text-sm text-teal-600">First Time In</div>
                            <div className="text-lg font-bold text-teal-700">
                                {stats.earliestStart ? parseUTC(stats.earliestStart).toLocaleDateString() : 'N/A'}
                            </div>
                        </div>
                        <div className="bg-orange-50 p-4 rounded-lg shadow border border-orange-200">
                            <div className="text-sm text-orange-600">Last Time Out</div>
                            <div className="text-lg font-bold text-orange-700">
                                {stats.latestCompletion ? parseUTC(stats.latestCompletion).toLocaleDateString() : 'N/A'}
                            </div>
                        </div>
                    </div>
                )}

                {/* Filter and Action Bar */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                    <div>
                        <select
                            value={statusFilter ?? ''}
                            onChange={(e) => setStatusFilter(e.target.value === '' ? null : parseInt(e.target.value))}
                            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                        >
                            <option value="">All Tasks</option>
                            <option value={TaskAssignmentStatus.Assigned}>Assigned</option>
                            <option value={TaskAssignmentStatus.InProgress}>In Progress</option>
                            <option value={TaskAssignmentStatus.Completed}>Completed</option>
                        </select>
                    </div>
                    <button
                        onClick={() => setShowCreateTask(true)}
                        className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700"
                    >
                        <i className="fas fa-plus mr-2"></i>
                        New Task
                    </button>
                </div>
            </div>
            </PageFadeSection>

            <PageFadeSection>
            {/* Task Cards */}
            {loading ? (
                <CardGridSkeleton cards={6} />
            ) : tasks.length === 0 ? (
                <div className="text-center py-12">
                    <i className="fas fa-tasks text-4xl text-gray-300 dark:text-gray-600 mb-4"></i>
                    <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300">No tasks assigned</h3>
                    <p className="text-gray-500 dark:text-gray-400">Tasks assigned to you will appear here</p>
                </div>
            ) : (
                renderTaskCards()
            )}
            </PageFadeSection>

            {/* Modals */}
            {renderCreateTaskModal()}
            {renderTaskDetailModal()}
        </div>
    );
};

export default MyAssignedTasksPage;